<template>
  <section
    class="c-product-info l-container"
    :class="{
      'c-product-info--mini-bundle': miniBundle
    }"
  >
    <div :class="gridLayoutClasses">
      <div
        class="c-product-info__content c-product-info__content--mobile"
        :class="[
          'l-grid__item',
          'u-hidden-desktop'
        ]"
      >
        <div class="c-product-info__overview">
          <h1
            class="c-product-info__title t-heading-5 u-margin-bottom-xx-small"
          >
            {{ title }}
          </h1>
          <slot name="rating" />
          <product-info-price
            :product="product"
            :size="size"
            :selected-variant="selectedVariant"
            :is-gift-card="isGiftCard"
          />
        </div>
      </div>
      <div
        class="c-product-info__media"
        :class="['l-grid__item', 'l-grid__item--justify-center']"
      >
        <media-display-carousel
          v-if="product"
          :media="filteredMedia"
          :available-for-sale="availableForSale"
          :clearance="isClearanceItem"
          :grid-layout="gridLayout"
        />
      </div>
      <div
        class="c-product-info__content"
        :class="[
          'l-grid__item',
          'l-grid__item--justify-center',
        ]"
      >
        <div class="c-product-info__overview u-hidden-mobile">
          <h1 class="c-product-info__title t-heading-5">
            {{ title }}
          </h1>
          <h2 class="t-heading-6--light">
            ENSO RINGS
          </h2>
          <slot name="rating" />
          <product-info-price
            v-if="showPriceVariant"
            :product="product"
            :size="size"
            :selected-variant="selectedVariant"
            :is-gift-card="isGiftCard"
          />
        </div>
        <product-info-collection
          v-if="visibleMap.mini"
          :product="product"
          :products="products"
        />
        <template v-if="visibleMap.base">
          <product-info-color-filter
            v-if="visibleMap.bundle"
            :product="product"
            :products="products"
          />
          <product-info-multi
            v-if="visibleMap.anyBundle"
            :active-bundle-type-product="activeBundleTypeProduct"
            :active-bundle-type-size="activeBundleTypeSize"
            :bundle-type-products="bundleTypeProducts"
            :bundle-single-size="bundleSingleSize"
            :is-gift-card="product | isGiftCard"
            :stock-status="stockStatus"
            :product="product"
            :is-bundle="isBundle"
            :variant-in-stock="variantInStock"
            :mini-bundle="miniBundle"
            :disable-filter-bar="!showFilterBar"
            @update:size="handleBundleOption"
            @update:product="handleBundleOption"
            @resetBundle="bundleState = {}"
            @modal:open="$emit('modal:open')"
            @engraving="$emit('engraving', $event)"
          >
            <product-info-add-to-cart
              :product="product"
              :size="selectedSize"
              :selected-variant="selectedVariant"
              :is-giftcard="product | isGiftCard"
              :is-bundle="isBundle"
              @engraving="$emit('engraving', $event)"
            >
              <!--
                all of these props will be passed into ProductAddToCart verbaitm
                imports in the direction of component-library->headless may be
                technically possible with some hacks, but not advisable.
                @link https://vuejs.org/v2/guide/components-slots.html#Scoped-Slots
              -->
              <slot
                v-if="product"
                :product="product"
                :products="products"
                :variant="selectedVariant"
                :metafields="product.metafields"
                :confirmed-selection="true"
                :all-options-selected="allOptionsSelected"
                :only-one-option="true"
                :show-add-to-cart="showAddToCart"
                :line-item-properties="lineItemProps"
                :addon-line-items="addonLineItems"
              />
            </product-info-add-to-cart>
          </product-info-multi>
          <template v-else-if="visibleMap.base">
            <product-info-color-filter
              v-if="product.variantOptions.length > 1"
              :product="product"
              :products="products"
            />
            <product-info-width
              :product="product"
              :products="products"
            />
            <product-info-size
              v-if="product.variantOptions.length > 1"
              :product="product"
              :size="size"
              :is-gift-card="product | isGiftCard"
              :variant-in-stock="variantInStock"
              :dropdown="dropdown"
              :size-guide-modal="sizeGuideModal"
              @update:size="$emit('update:size', $event)"
              @modal:open="$emit('modal:open')"
            />
            <product-info-add-to-cart
              class="c-product-info-filter"
              :product="product"
              :size="selectedSize"
              :selected-variant="selectedVariant"
              :show-add-to-cart="showAddToCart"
              :is-gift-card="product | isGiftCard"
              :is-bundle="isBundle"
              @engraving="$emit('engraving', $event)"
            >
              <!--
                all of these props will be passed into ProductAddToCart verbaitm
                imports in the direction of component-library->headless may be
                technically possible with some hacks, but not advisable.
                @link https://vuejs.org/v2/guide/components-slots.html#Scoped-Slots
              -->
              <slot
                v-if="product"
                :product="product"
                :variant="selectedVariant"
                :metafields="product.metafields"
                :confirmed-selection="true"
                :all-options-selected="allOptionsSelected"
                :only-one-option="true"
                :show-add-to-cart="showAddToCart"
                :line-item-properties="lineItemProps"
                :addon-line-items="addonLineItems"
              />
            </product-info-add-to-cart>
          </template>
        </template>
        <slot name="recommendations" />

        <div class="dk-backend-static" :data-product-id="productSourceId"></div>
      </div>
    </div>
  </section>
</template>
<script>
import abTesting from '@mixins/abTesting';
import clone from 'lodash/clone'
import get from 'lodash/get'
import isArray from 'lodash/isArray'
import MediaDisplayCarousel from '../mediaDisplay/MediaDisplayCarousel'
import ProductInfoAddToCart from './ProductInfoAddToCart'
import ProductInfoCollection from './ProductInfoCollection'
import ProductInfoColorFilter from './ProductInfoColorFilter'
import ProductInfoPrice from './ProductInfoPrice'
import uniqueId from 'lodash/uniqueId'
import { formatPackingSlipProp, getBundleMetafields } from '@/utils/bundleHelper'
import { mapState } from 'vuex'
import { queryProducts } from '@/utils/queryProducts'

const isGiftCard = (product) => (
  product &&
  product.vendor &&
  product.vendor.toLowerCase() === "rise.ai"
)
export default {
  components: {
    MediaDisplayCarousel,
    ProductInfoAddToCart,
    ProductInfoCollection,
    ProductInfoColorFilter,
    ProductInfoMulti: () => import(
      /* webpackPrefetch: true */
      './ProductInfoMulti'
    ),
    ProductInfoPrice,
    ProductInfoSize: () => import(
      /* webpackPrefetch: true */
      './ProductInfoSize'
    ),
    ProductInfoWidth: () => import(
      /* webpackPrefetch: true */
      './ProductInfoWidth'
    ),
  },
  filters: {
    size(product) {
      const selectedOptions = get(product, 'variant.selectedOptions') || []
      for (let options of selectedOptions) {
        if (isArray(options)) {
          for (let item of options) {
            if (item && item.name && (item.name.toLowerCase() === 'size')) {
              return item.value
            }
          }
        }
      }
    },
    isClearanceItem(product) {
      const tags = get(product, 'tags') || []
      return tags.includes('clearance')
    },
    isGiftCard,
  },
  mixins: [
    abTesting
  ],
  props: {
    product: {
      type: Object,
      default: () => ({})
    },
    products: {
      type: Array,
      default: () => []
    },
    size: {
      type: [Number, String],
      default: null
    },
    bundleProducts: {
      type: Array,
      default: () => []
    },
    bundleOptions: {
      type: Object,
      default: () => ({})
    },
    variantAvailable: {
      type: Boolean,
      default: true
    },
    miniProductDetail: {
      type: Boolean,
      default: false
    },
    miniBundle: {
      type: Boolean,
      default: false
    },
    dropdown: {
      type: Boolean,
      default: true
    },
    sizeGuideModal: {
      type: Boolean,
      default: false
    },
    gridLayout: {
      type: Boolean,
      default: false
    },
    disableFilterBar: {
      type: Boolean,
      default: true
    },
    productSourceId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      bundleState: null,
      variantInStock: {},
      outOfStock: false,
      stockContext: null,
      stockLoading: false,
    }
  },
  computed: {
    showFilterBar() {
      const disabled = this.disableFilterBar
      const bundleOptions = this.bundleOptions
      return !!(!disabled || bundleOptions.showFilter)
    },
    gridLayoutClasses() {
      const miniProductDetail = this.miniProductDetail
      const miniBundle = this.miniBundle
      let results = ['l-grid l-grid--align-start']
      if (miniProductDetail || miniBundle) {
        results.push('c-product-info__layout')
      } else {
        results.push('l-grid--2-col-first-long')
      }
      return results
    },
    visibleMap() {
      const isBundle = this.isBundle
      const miniProductDetail = this.miniProductDetail
      const miniBundle = this.miniBundle
      return {
        mini: miniProductDetail,
        bundle: isBundle && !miniBundle,
        anyBundle: isBundle,
        miniBundle: isBundle && miniBundle,
        base: !miniProductDetail
      }
    },
    stockStatus() {
      return {
        context: this.stockContext,
        loading: this.stockLoading,
        outOfStock: this.outOfStock,
      }
    },
    allOptionsSelected() {
      const bundleOptionsSelected = this.bundleOptionsSelected
      const isBundle =  this.isBundle
      const isGiftCard = this.isGiftCard
      const variant = this.selectedVariant
      let result = false
      if (isBundle) {
        result = bundleOptionsSelected
      } else if (!isBundle) {
        // !variant && !isGiftCard -> disable atc
        // isGiftCard && !variant -> disable atc
        // disable atc -> !allVariantsSelected
        result = !(
          (!variant && !isGiftCard) ||
          (isGiftCard && !variant)
        )
      }
      return result
    },
    bundleSingleSize() {
      const bundleOptions = this.bundleOptions
      return !!get(bundleOptions, 'singleSize', false)
    },
    bundleOptionsSelected() {
      const isBundle =  this.isBundle
      const bundleProps = this.bundleProps
      let result = false
      if (
        isBundle &&
        bundleProps &&
        bundleProps.length
      ) {
        for (let prop of bundleProps) {
          if (
            prop &&
            prop.key === '_configurator_data' &&
            prop.value &&
            prop.value.items
          ) {
            for (let propItem of prop.value.items) {
              result = !!(propItem && propItem.part_id)
              if (!result) {
                break
              }
            }
            if (!result) {
              break
            }
          }
        }
      }
      return result
    },
    selectedSize() {
      const isBundle = this.isBundle
      const size = this.size
      let bundleSize = 0
      for (let value of Object.values(this.activeBundleTypeSize)) {
        bundleSize += parseInt(value, 10)
      }
      return (isBundle) ? bundleSize : size
    },
    lineItemProps() {
      const bundleProps = this.bundleProps
      let result = []
      if (bundleProps && bundleProps.length) {
        result.push(...bundleProps)
      }
      return result
    },
    // this is probably unique enough
    bundleId() {
      const isBundle = this.isBundle
      const uid = this._uid
      const { handle } = this.product
      return (isBundle) ? uniqueId(`${handle}-${uid}`) : null
    },
    bundleProps() {
      const bundleId = this.bundleId
      const bundleOptions = this.bundleOptions
      const activeBundleTypeProduct = this.activeBundleTypeProduct
      const addonLineItemHandles = this.addonLineItemHandles
      const product = this.product
      let props = []
      let configItems = []
      let packingSlipItemOptions = []
      let bundleEngravingData = []
      let uiOptions = []
      let optionIndex = 1
      let lineItems = []
      for (let item of Object.values(activeBundleTypeProduct || {})) {
        if (!item) {
          console.warn({ bundleOptions, activeBundleTypeProduct })
          throw new Error('InvalidBundleData', { cause: { bundleOptions,
            activeBundleTypeProduct } })
        }
        if (addonLineItemHandles.includes(item.handle)) {
          continue
        }
        let {
          uiOption,
          configItem,
          packingSlipItemOption
        } = getBundleMetafields({ item, optionIndex })
        // add generated props to their respective lists
        lineItems.push(item)
        configItems.push(configItem)
        packingSlipItemOptions.push(packingSlipItemOption)
        uiOptions.push({
          ...uiOption,
          engravingState: item.engravingState ? item.engravingState : {}
        })
        if (item && item.engravingState && item.engravingState.props) {
          // example
          // Dualtone Silicone Ring - Obsidian / Yellow - 6 / Obsidian / Yellow
          bundleEngravingData.push({
            option: `${item.title} - ${item.variant.title}`,
            engraving: item.engravingState.props
          })
        }
        optionIndex += 1
      }
      let insertCard = product.metafieldsMap?.insert_card_id
      if (insertCard && insertCard.value) {
        packingSlipItemOptions.push(insertCard.value)
      }
      if (packingSlipItemOptions && packingSlipItemOptions.length) {
        props.push(formatPackingSlipProp({ packingSlipItemOptions }))
      }
      if (uiOptions && uiOptions.length) {
        props.push(...uiOptions)
      }
      if (configItems && configItems.length) {
        // console.log(configItems)
        props.push({
          key: '_configurator_data',
          value: {
            type: bundleOptions.type || 'items',
            items: configItems
          }
        })
      }
      if (lineItems && lineItems.length) {
        props.push({
          key: '_local_bundle_selections',
          value: lineItems
        })
      }
      if (bundleEngravingData && bundleEngravingData.length) {
        props.push({
          key: '_bundle_engraving_data',
          value: bundleEngravingData
        })
      }
      props.push({
        key: '_local_bundle_id',
        value: bundleId
      }, {
        key: '_local_addon_gwp'
      })
      return (props.length) ? clone(props) : []
    },
    isBundle() {
      const productType = this.productType
      const bundleProducts = this.bundleProducts
      const bundleOptions = this.bundleOptions
      return !!(
        (productType === 'BUNDLE') &&
        (
          (bundleProducts && bundleProducts.length) &&
          (bundleOptions && Object.keys(bundleOptions || {}).length)
        )
      )
    },
    activeBundleTypeSize() {
      const bundleState = this.bundleState
      let result = {}
      for (let [ key, value ] of Object.entries(bundleState || {})) {
        result[key] = value.size
      }
      return result
    },
    activeBundleTypeHandle() {
      const bundleState = this.bundleState
      const bundleTypeProducts = this.bundleTypeProducts
      let result = {}
      for (let [ key, products ] of Object.entries(bundleTypeProducts || {})) {
        const handle = get(bundleState, `['${key}'].handle`)
        // default to the first color product
        result[key] = (handle) ? handle : get(products, '[0].handle')
      }
      return result
    },
    activeBundleTypeProduct() {
      const bundleTypeProducts = this.bundleTypeProducts
      const activeBundleTypeSize = this.activeBundleTypeSize
      const activeBundleTypeHandle = this.activeBundleTypeHandle
      const bundleState = this.bundleState

      let result = {}
      for (let [ key, products ] of Object.entries(bundleTypeProducts || {})) {
        const size = activeBundleTypeSize[key]
        const handle = activeBundleTypeHandle[key]
        let product
        for (let item of products) {
          if (item.handle === handle) {
            product = item
            break
          }
        }
        if (product) {
          result[key] = {
            ...product,
            variant: (size) ? this.getSelectedVariant(product, size) : null
          }
        } else {
          result[key] = products[0]
        }
      }

      for (let [ key, value ] of Object.entries(bundleState || {})) {
        if(value.engraving) {
          result[key].engravingState = value.engraving
        }
      }
      return result
    },
    bundleTypeProducts() {
      const bundleOptions = this.bundleOptions
      const bundleProducts = this.bundleProducts
      let result = {}
      if (bundleOptions && bundleOptions.groupBy) {
        result = queryProducts(bundleProducts, {
          type: 'groupBy',
          ...bundleOptions.groupBy
        })
      }
      return result
    },
    showAddToCart() {
      const product = this.product
      const selectedVariant = this.selectedVariant
      const variantAvailable = this.variantAvailable
      return (
        // show add to cart of a variant is selected and it is available for
        // sale
        (product && selectedVariant && selectedVariant.availableForSale && variantAvailable) ||
        // show add to cart if no variant has been selected yet
        (product && !selectedVariant)
      )
    },
    availableForSale() {
      const selectedVariant = this.selectedVariant
      return selectedVariant ? selectedVariant.availableForSale : true
    },
    isClearanceItem() {
      const product = this.product
      const tags = get(product, 'tags') || []
      return tags.includes('clearance')
    },
    filteredMedia() {
      const product = this.product
      const abTesting = this.abTesting
      let result = []
      let denyList = [
        "Bree",
        "Lato",
        "Pacifico",
        "Baloo",
        "Paytone",
        "Russo",
        "Sideview",
      ]
      for (let image of product.media || []) {
        let included = false
        for (let i = 0; i < denyList.length; i++) {
          if (image.altText || image.alt) {
            let altText = image.altText ? image.altText : image.alt
            if (altText.includes(denyList[i])) {
              if (denyList[i] == "Bree" && altText.includes('Breeze')){
                included = false
              } else {
                included = true
                i = denyList.length
              }
            }
          }
        }
        if (!included) {
          result.push(image)
        }
      }
      if(abTesting) {
        let warrantyLight = {
          src: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Variation_1_-_Image_2_1.png',
          thumbnailSrc: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Variation_1_-_Image_2_1.png?width=100',
          type: 'image',
        }
        let lifetimeGuarantee = {
          src: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Variation_3_-_Image_1_1.png.png',
          thumbnailSrc: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Variation_3_-_Image_1_1.png?width=100',
          type: 'image',
        }
        let warrantyRing = {
          src: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Variation_1_-_Image_1_1.png',
          thumbnailSrc: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Variation_1_-_Image_1_1.png?width=100',
          type: 'image'
        }
        let gridImage = {
          src: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Variation_2_-_Image_1_1.png',
          thumbnailSrc: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Variation_2_-_Image_1_1.png?width=100',
          type: 'image'
        }

        // Infographic A/B test
        let lifetimeWarranty = {
          src: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Backed_up_by_lifetime_warranty.png',
          thumbnailSrc: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Backed_up_by_lifetime_warranty.png?width=100',
          type: 'image'
        }
        let madeInRockies = {
          src: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Made_in_the_rockies_with_love.png',
          thumbnailSrc: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Made_in_the_rockies_with_love.png?width=100',
          type: 'image'
        }
        let coveredLifetimeWarranty = {
          src: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Covered_by_lifetime_warranty.png',
          thumbnailSrc: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Covered_by_lifetime_warranty.png?width=100',
          type: 'image'
        }
        let etchedInRockies = {
          src: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Etched_in_the_rockies.png',
          thumbnailSrc: 'https://cdn.shopify.com/s/files/1/0018/8612/6193/files/Etched_in_the_rockies.png?width=100',
          type: 'image'
        }

        if (this.galleryImage.variant1) {
          result.splice(4, 0, warrantyRing, warrantyLight)
        } else if (this.galleryImage.variant2) {
          result.splice(5, 0, gridImage)
        } else if (this.galleryImage.variant3) {
          result.splice(5, 0, lifetimeGuarantee)
        }

        //Infographic A/B Test
        if (this.infographic.variant1) {
          result.splice(1,0,madeInRockies)
        } else if (this.infographic.variant2) {
          result.splice(1,0,lifetimeWarranty)
        } else if (this.infographic.variant3) {
          result.splice(1,0,coveredLifetimeWarranty)
        } else if (this.infographic.variant4) {
          result.splice(1,0,etchedInRockies)
        } else if (this.infographic.variant5) {
          result.splice(1,0,lifetimeGuarantee)
        }
      }
      //console.log("result test", result)
      return result
    },
    showPriceVariant() {
      let result = false
      const abTesting = this.abTesting
      const productInfoType = this.productInfoType
      if(abTesting && (productInfoType.default || productInfoType.variant1 || productInfoType.variant2)) {
        result = true
      }
      if (this.miniProductDetail) {
        result = true
      }
      return result
    },
    productType() {
      const product = this.product
      let result = 'RING'
      if (
        product &&
        product.productType
      ) {
        result = product.productType.toUpperCase()
      }
      return result
    },
    title() {
      return get(this.product, 'title', null)
    },
    selectedVariant() {
      const product = this.product
      const size = this.size
      const isBundle = this.isBundle
      const bundleOptionsSelected = this.bundleOptionsSelected
      let result = null
      try {
        if (isBundle) {
          result = (bundleOptionsSelected) ? get(product, 'variants[0]') : null
        } else {
          result = this.getSelectedVariant(product, size)
        }
      } catch (err) {
        console.warn(err, 'are you trying to setup a bundle? you might ',
          'have forgotten to set the productType field.'
        )
      }
      return result
    },
    isGiftCard() {
      return isGiftCard(this.product)
    },
    addonLineItems() {
      const bundleId = this.bundleId
      const isBundle = this.isBundle
      const { packageSeparately } = this.bundleOptions
      const activeBundleTypeProduct = this.activeBundleTypeProduct
      let result = []
      if (isBundle && packageSeparately) {
        const products = Object.values(activeBundleTypeProduct || {})
        for (let item of queryProducts(products, {
          type: 'selectBy',
          method: 'AND',
          field: 'handle',
          tests: packageSeparately
        })) {
          result.push({
            product: item,
            variant: item.variant,
            quantity: item.quantity,
            customAttributes: [
              { key: '_lineGiftWithPurchase' },
              { key: '_local_addon_gwp' },
              { key: '_local_bundle_id', value: bundleId }
            ]
          })
        }
      }
      return result
    },
    addonLineItemHandles() {
      return this.addonLineItems.map(({ product }) => product.handle)
    },
    ...mapState("vwo", ['productInfoType','galleryImage','infographic']),
  },
  watch: {
    selectedVariant(variant) {
      this.$emit('update:variant', variant)
    },
    async activeBundleTypeProduct(activeProducts) {
      let available = []
      for (let [bundleType, product] of Object.entries(activeProducts)) {
        if (product && product.variant) {
          const availableForSale = await this.updateVariantStock(
            bundleType,
            product,
            product.variant
          )
          available.push({
            bundleType,
            availableForSale,
            variantId: product.variant.id,
            size: product.variant.selectedOptions.find(({ name }) => {
              return name === 'Size'
            })
          })
        }
      }
      for (let { bundleType, availableForSale, variantId, size } of available) {
        if (!availableForSale) {
          this.outOfStock = true
          this.stockContext = {
            size: size && size.value,
            bundleType,
            variantId,
            availableForSale,
          }
          // this.singleSize = false
          break
        }
      }
    },
  },
  created() {
    //
  },
  mounted() {
    this.updateStockData()
    this.updateBundleVariant()
    this.setupShoppingGivesObserver()
  },
  methods: {
    updateBundleVariant() {
      const isBundle = this.isBundle
      const variant = get(this.product, 'variants[0]')
      if (isBundle && variant) {
        this.$emit('update:variant', variant)
      }
    },
    /**
     * code to change shopping gives text when it is loaded for Nurses day.
     */
    setupShoppingGivesObserver() {
      const observer = new MutationObserver(() => {
        const element = document.getElementsByClassName('unselectedText')[0]
        let isThereStyle = false
        try {
          element.style.fontFamily = 'Futura, Arial, sans-serif !important'
          isThereStyle = true
        } catch (e) {
          isThereStyle = false
        }
        // console.log(isThereStyle)
        if (isThereStyle) {
          element.innerText = ` the American Nurses Foundation. Choose "Select Cause" to activate and Enso will donate on your behalf!`
          element.style.fontFamily = "Futura, Arial, sans-serif !important"
          element.style.fontWeight = "normal !important"
          observer.disconnect()
        }
      })
      observer.observe(document, {
        attributes: false, childList: true, characterData: false, subtree: true
      })
    },
    async updateStockData() {
      const start = Date.now()
      this.stockLoading = true
      const bundleOptions = this.bundleOptions
      const bundledCollections = get(bundleOptions, 'bundledCollections') || []
      const bundleTypeProducts = this.bundleTypeProducts
      const bundleTypeEntries = Object.entries(bundleTypeProducts || {})
      let promises = []
      try {
        // fetch bundle component stock
        for (let collection of bundledCollections) {
          promises.push(
            this.$api.checkStockAvailable({ collection })
          )
        }
        // fetch base product stock
        promises.push(
          this.updateVariantStock(
            this.product.handle,
            this.product
          )
        )
        // process response
        const results = await Promise.all(promises)
        for (let result of results) {
          // console.log('stock-data', result)
          if (result) {
            for (let [ bundleType, products ] of bundleTypeEntries) {
              for (let { handle } of products) {
                this.setVariantStock(bundleType, result[handle])
              }
            }
          }
        }
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            `[update-stock-data] OK (${promises.length} requests in ${Date.now() - start}ms)`
          )
        }
        this.stockLoading = false
      } catch (err) {
        console.warn('[update-stock-data] ERR', err, promises)
        this.stockLoading = false
      }
    },
    setVariantStock(bundleType, stockData) {
      this.variantInStock = {
        ...this.variantInStock,
        ...stockData,
        [bundleType]: {
          ...(get(this.variantInStock, bundleType) || {}),
          ...stockData
        }
      }
      /*
      if (process.env.NODE_ENV !== 'production') {
        window.$bundleStockData = this.variantInStock
      }
      */
    },
    /**
     * @param {string} bundleType
     * @param {object} product
     * @param {object} variant
     */
    async updateVariantStock(bundleType, product, variant) {
      const stockData = await this.$api.checkStockAvailable({ product })
      this.setVariantStock(bundleType, stockData)
      if (variant) {
        return get(stockData, `${variant.id}.availableForSale`) || false
      }
    },
    getSelectedVariant(product, size) {
      const isGiftCardProduct = isGiftCard(product)
      let result = null
      for (let variant of product.variants || []) {
        const sizeFieldName = (isGiftCardProduct) ? 'Title' : 'Size'
        let sizeObj
        for (let option of variant.selectedOptions) {
          if (option.name === sizeFieldName) {
            sizeObj = option
            break
          }
        }
        if (sizeObj && sizeObj.value == size) {
          result = variant
          break
        }
      }
      return result
    },
    handleBundleOption({ bundleType, type, event }, engravingState) {
      this.bundleState = {
        ...(this.bundleState || {}),
        [bundleType]: {
          ...(get(this.bundleState, bundleType) || {}),
          [type]: event,
          ["engraving"]: engravingState
        },
      }
    }
  },
}
</script>
<style lang="scss" scoped>
.c-product-info {
  &--mini-bundle {
    position: relative;
    height: rem(800px);
    @include respond-min(desktop) {
      height: rem(1400px);
    }
    @include respond(tab-land) {
      height: rem(1400px);
    }
    @include respond(phone) {
      height: auto;
    }
  }
  &__layout {
    grid-template-columns: 1.5fr 1fr;
    @include respond(tab-land) {
      grid-template-columns: 1fr;
    }
  }
  &__content {
    width: 100%;
    max-width: rem(600px);
  }
  &__description {
    @include themify($themes) {
      color: themed('foreground', 'light-text');
    }
  }
  /**
   * sezzle is injected between these two elements, this margin holds the
   * space between them until the sezzle script loads.
   */
  &__overview + &__color-filter {
    margin-top: rem(36px);
  }
  &__price-max {
    text-decoration: line-through;
    @include themify($themes) {
      color: themed("foreground", "light");
    }
  }
  &__percent-off {
    @include themify($themes) {
      background: themed("accent", "base");
    }
    padding: rem(0px) rem(10px);
    border-radius: rem(30px);
    margin-left: rem(8px);
  }
  &__ring-width-display > * {
    display: inline-block;
    padding-right: rem(10px);
  }
  &__personalize {
    text-align: center;
  }
  &__add-to-cart {
    text-align: center;
  }
  &__color-heading {
    display: inline-block;
    margin-right: rem(10px)
  }
  &__size-filter,
  &__ring-width {
    padding: rem(20px) 0;
    @include themify($themes) {
      border-top: $border themed("foreground", "section-border");
    }
  }
  &__color-filter {
    padding: rem(20px) 0;
    @include themify($themes) {
      border-top: $border themed("foreground", "section-border");
    }
    @include respond(tab-land) {
      border-top: none;
    }
  }
  &-filter {
    padding: rem(20px) 0;
    @include themify($themes) {
      border-top: $border themed("foreground", "section-border");
    }
  }
}

#shopping-gives-widget {
  height: rem(80px);
}
</style>
